// Offline beat tracking for a whole song (used when the page plays a song
// file, so every beat is known before it is heard).
//
// 1. Onset envelope from OnsetDetector.
// 2. Overall tempo from estimateTempo.
// 3. Dynamic programming (Ellis 2007, "Beat Tracking by Dynamic
//    Programming"): choose the chain of beats that lands on strong onsets
//    while keeping gaps close to the tempo. This follows small tempo drift
//    that a fixed grid would slowly fall off.

import { HOP_SIZE, OnsetDetector, type OnsetFrame } from "./onset.ts";
import { estimateTempo } from "./tempo.ts";

export interface SongAnalysis {
  bpm: number;
  // Beat times in seconds from the start of the audio.
  beats: number[];
  duration: number;
}

// How strongly gaps are held to the tempo (Ellis uses ~100 with an envelope
// scaled to unit standard deviation).
const TIGHTNESS = 100;

export function analyzeSong(samples: Float32Array, sampleRate: number): SongAnalysis | null {
  const frames = new OnsetDetector(sampleRate).push(samples, 0);
  return trackBeats(frames, sampleRate, samples.length / sampleRate);
}

// The beat tracking itself, for callers that already ran an OnsetDetector.
export function trackBeats(frames: OnsetFrame[], sampleRate: number, duration: number): SongAnalysis | null {
  const n = frames.length;
  const frameRate = sampleRate / HOP_SIZE;
  if (n < frameRate * 6) return null;

  const flux = frames.map((f) => f.flux);
  const tempo = estimateTempo(flux, frameRate, frames[n - 1]!.time);
  if (!tempo) return null;
  const period = (60 * frameRate) / tempo.bpm;

  // Detrended, unit-variance envelope.
  const env = onsetEnvelope(flux, frameRate);
  let sq = 0;
  for (let i = 0; i < n; i++) sq += env[i]! * env[i]!;
  const std = Math.sqrt(sq / n);
  if (std === 0) return null;
  for (let i = 0; i < n; i++) env[i] = env[i]! / std;

  // score[t]: best total for a chain of beats ending at frame t.
  const score = new Float64Array(n);
  const back = new Int32Array(n).fill(-1);
  const minGap = Math.max(1, Math.round(period / 2));
  const maxGap = Math.round(period * 2);
  for (let t = 0; t < n; t++) {
    let best = 0;
    let arg = -1;
    for (let p = Math.max(0, t - maxGap); p <= t - minGap; p++) {
      const penalty = Math.log((t - p) / period) ** 2;
      const v = score[p]! - TIGHTNESS * penalty;
      if (v > best) {
        best = v;
        arg = p;
      }
    }
    score[t] = env[t]! + best;
    back[t] = arg;
  }

  // End on the best-scoring frame within the last beat period, then trace back.
  let end = n - 1;
  for (let t = Math.max(0, n - Math.ceil(period)); t < n; t++) if (score[t]! > score[end]!) end = t;
  const chain: number[] = [];
  for (let t = end; t >= 0; t = back[t]!) chain.push(t);
  chain.reverse();

  // Drop beats in a quiet intro/outro: the chain still places beats there,
  // but with no real onsets under them.
  const strengths = chain.map((i) => {
    let local = 0;
    for (let j = Math.max(0, i - 2); j <= Math.min(n - 1, i + 2); j++) local = Math.max(local, env[j]!);
    return local;
  });
  const typical = [...strengths].sort((a, b) => a - b)[Math.floor(strengths.length / 2)] ?? 0;
  const real = (k: number) => strengths[k]! >= 0.25 * typical;
  let first = 0;
  while (first < chain.length - 1 && !real(first)) first++;
  let last = chain.length - 1;
  while (last > first && !real(last)) last--;

  const beats = chain.slice(first, last + 1).map((i) => frames[i]!.time);
  return { bpm: tempo.bpm, beats, duration };
}

// Onset flux minus its ~0.25 s running average, floored at 0: the rises only.
export function onsetEnvelope(flux: ArrayLike<number>, frameRate: number): Float64Array {
  const n = flux.length;
  const half = Math.max(1, Math.round(frameRate * 0.125));
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i]! + flux[i]!;
  const env = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - half);
    const hi = Math.min(n, i + half + 1);
    env[i] = Math.max(0, flux[i]! - (prefix[hi]! - prefix[lo]!) / (hi - lo));
  }
  return env;
}

// Straight-line fit through beat times: the tempo and phase to broadcast for
// an upcoming stretch of the song.
export function fitBeatGrid(beatsMs: number[]): { beatMs: number; firstMs: number } | null {
  const k = beatsMs.length;
  if (k < 3) return null;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < k; i++) {
    const y = beatsMs[i]!;
    sx += i;
    sy += y;
    sxx += i * i;
    sxy += i * y;
  }
  const beatMs = (k * sxy - sx * sy) / (k * sxx - sx * sx);
  const firstMs = (sy - beatMs * sx) / k;
  return beatMs > 0 ? { beatMs, firstMs } : null;
}
